import {
  buildCollection,
  buildProperty,
  useDataSource,
  useSnackbarController,
} from "@firecms/core";
import { Button, CircularProgress } from "@firecms/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminModal } from "../../custom/AdminModal";
import { useAdminTranslation } from "../../i18n/useAdminTranslation";
import {
  EconomyDocId,
  emptyEconomyCoins,
  type EconomyCoins,
  type ParticipantEconomy,
} from "../../types/economy";
import {
  RankingDocId,
  emptyIndividualRankingScore,
  emptyTeamRankingScore,
  type IndividualRankingScore,
  type ParticipantRanking,
  type TeamRankingScore,
} from "../../types/ranking";
import { getEventPath } from "../../utils";
import { getModeCollectionIds } from "../../workspace/adminMode";
import { useAdminWorkspace } from "../../workspace/useAdminWorkspace";
import { registerOpenParticipantEconomyDialog } from "./participantEconomyBridge";

const economyCoinsCollection = buildCollection<EconomyCoins>({
  name: "Coins",
  id: "economy_coins",
  path: "economy",
  properties: {
    balance: buildProperty({ name: "Balance", dataType: "number" }),
    updatedAt: buildProperty({ name: "Updated", dataType: "date" }),
  },
});

const individualRankingCollection = buildCollection<IndividualRankingScore>({
  name: "Individual ranking",
  id: "ranking_individual",
  path: "ranking",
  properties: {
    score: buildProperty({ name: "Score", dataType: "number" }),
    updatedAt: buildProperty({ name: "Updated", dataType: "date" }),
  },
});

const teamRankingCollection = buildCollection<TeamRankingScore>({
  name: "Team ranking",
  id: "ranking_team",
  path: "ranking",
  properties: {
    score: buildProperty({ name: "Score", dataType: "number" }),
    updatedAt: buildProperty({ name: "Updated", dataType: "date" }),
  },
});

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-600 dark:bg-slate-900/60">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
        {value}
      </div>
      {hint ? (
        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function formatUpdatedAt(value: Date | undefined, emptyLabel: string): string {
  if (!value) {
    return emptyLabel;
  }
  return value.toLocaleString();
}

export function ParticipantEconomyDialogHost() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [participantUid, setParticipantUid] = useState<string | null>(null);
  const [economy, setEconomy] = useState<ParticipantEconomy | null>(null);
  const [ranking, setRanking] = useState<ParticipantRanking | null>(null);

  const { mode } = useAdminWorkspace();
  const eventId = useMemo(
    () => getModeCollectionIds(mode).event ?? null,
    [mode]
  );
  const dataSource = useDataSource();
  const snackbarController = useSnackbarController();
  const { t: ta } = useAdminTranslation();

  const close = useCallback(() => {
    if (loading) {
      return;
    }
    setOpen(false);
    setParticipantUid(null);
    setEconomy(null);
    setRanking(null);
  }, [loading]);

  const loadParticipantState = useCallback(
    async (uid: string) => {
      if (!eventId) {
        return;
      }

      setLoading(true);
      setEconomy(null);
      setRanking(null);
      try {
        const participantPath = `${getEventPath(eventId)}/participant/${uid}`;
        const [coinsEntity, individualEntity, teamEntity] = await Promise.all([
          dataSource.fetchEntity<EconomyCoins>({
            path: `${participantPath}/economy`,
            entityId: EconomyDocId.COINS,
            collection: economyCoinsCollection,
          }),
          dataSource.fetchEntity<IndividualRankingScore>({
            path: `${participantPath}/ranking`,
            entityId: RankingDocId.INDIVIDUAL,
            collection: individualRankingCollection,
          }),
          dataSource.fetchEntity<TeamRankingScore>({
            path: `${participantPath}/ranking`,
            entityId: RankingDocId.TEAM,
            collection: teamRankingCollection,
          }),
        ]);

        setEconomy({
          coins: coinsEntity?.values ?? null,
        });
        setRanking({
          individual: individualEntity?.values ?? null,
          team: teamEntity?.values ?? null,
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : ta("common.unknownError");
        snackbarController.open({
          message: ta("participantEconomy.error", { message }),
          type: "error",
        });
        console.error(error);
        setOpen(false);
        setParticipantUid(null);
      } finally {
        setLoading(false);
      }
    },
    [dataSource, eventId, snackbarController, ta]
  );

  useEffect(() => {
    return registerOpenParticipantEconomyDialog((uid) => {
      if (!eventId) {
        snackbarController.open({
          message: ta("common.selectActiveEvent"),
          type: "warning",
        });
        return;
      }
      setParticipantUid(uid);
      setOpen(true);
      void loadParticipantState(uid);
    });
  }, [eventId, loadParticipantState, snackbarController, ta]);

  const individual = ranking?.individual ?? emptyIndividualRankingScore();
  const team = ranking?.team ?? emptyTeamRankingScore();
  const coins = economy?.coins ?? emptyEconomyCoins();
  const missingAll =
    !loading &&
    economy != null &&
    ranking != null &&
    economy.coins == null &&
    ranking.individual == null &&
    ranking.team == null;

  return (
    <AdminModal
      open={open}
      onClose={close}
      ariaLabel={ta("participantEconomy.ariaLabel")}
      title={ta("participantEconomy.title")}
      description={
        participantUid
          ? ta("participantEconomy.description", { uid: participantUid })
          : undefined
      }
      footer={
        <Button onClick={close} disabled={loading} color="primary">
          {ta("participantEconomy.close")}
        </Button>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <CircularProgress />
        </div>
      ) : missingAll ? (
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {ta("participantEconomy.empty")}
        </p>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
              {ta("participantEconomy.scoresSection")}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <StatCard
                label={ta("participantEconomy.individualScore")}
                value={String(individual.score)}
                hint={
                  ranking?.individual
                    ? formatUpdatedAt(
                        ranking.individual.updatedAt,
                        ta("participantEconomy.neverUpdated")
                      )
                    : ta("participantEconomy.missingDoc")
                }
              />
              <StatCard
                label={ta("participantEconomy.teamScore")}
                value={String(team.score)}
                hint={
                  ranking?.team
                    ? formatUpdatedAt(
                        ranking.team.updatedAt,
                        ta("participantEconomy.neverUpdated")
                      )
                    : ta("participantEconomy.missingDoc")
                }
              />
            </div>
          </div>

          <div>
            <div className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
              {ta("participantEconomy.coinsSection")}
            </div>
            <StatCard
              label={ta("participantEconomy.coinsBalance")}
              value={String(coins.balance)}
              hint={
                economy?.coins
                  ? formatUpdatedAt(
                      economy.coins.updatedAt,
                      ta("participantEconomy.neverUpdated")
                    )
                  : ta("participantEconomy.missingDoc")
              }
            />
          </div>
        </div>
      )}
    </AdminModal>
  );
}
